
import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "@/hooks/use-toast";
import {
  MessageSquare, ThumbsUp, ThumbsDown, CheckCircle2, Eye, Search,
  Plus, Award, Crown, Star, TrendingUp, Users, HelpCircle, Clock
} from "lucide-react";

const CATEGORIES = [
  { value: "accounting", label: "Accounting" },
  { value: "gst", label: "GST" },
  { value: "business", label: "Business" },
  { value: "tax", label: "Tax" },
  { value: "compliance", label: "Compliance" },
  { value: "general", label: "General" },
];

const ROLE_BADGES: Record<string, { label: string; color: string }> = {
  super_admin: { label: "Admin", color: "bg-destructive text-destructive-foreground" },
  company_admin: { label: "Business", color: "bg-primary text-primary-foreground" },
  ca: { label: "CA Expert", color: "bg-accent text-accent-foreground" },
  accountant: { label: "Accountant", color: "bg-secondary text-secondary-foreground" },
};

function ReputationBadge({ score }: { score: number }) {
  if (score >= 100) return <span className="inline-flex items-center gap-1 text-xs font-bold text-amber-500"><Crown className="h-3 w-3" />Gold</span>;
  if (score >= 50) return <span className="inline-flex items-center gap-1 text-xs font-bold text-slate-400"><Star className="h-3 w-3" />Silver</span>;
  if (score >= 10) return <span className="inline-flex items-center gap-1 text-xs font-bold text-orange-400"><Award className="h-3 w-3" />Bronze</span>;
  return null;
}

export default function CommunityForumPage() {
  const { user, role } = useAuth();
  const queryClient = useQueryClient();
  const [showAsk, setShowAsk] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [newQ, setNewQ] = useState({ title: "", content: "", category: "general", tags: "" });
  const [newAnswer, setNewAnswer] = useState("");

  // Fetch questions
  const { data: questions = [] } = useQuery({
    queryKey: ["forum-questions", categoryFilter],
    queryFn: async () => {
      let q = supabase.from("forum_questions").select("*").order("created_at", { ascending: false });
      if (categoryFilter !== "all") q = q.eq("category", categoryFilter);
      const { data, error } = await q;
      if (error) throw error;
      return data;
    },
  });

  // Fetch answers for selected question
  const { data: answers = [] } = useQuery({
    queryKey: ["forum-answers", selectedQuestion],
    queryFn: async () => {
      if (!selectedQuestion) return [];
      const { data, error } = await supabase.from("forum_answers").select("*").eq("question_id", selectedQuestion).order("upvotes", { ascending: false });
      if (error) throw error;
      return data;
    },
    enabled: !!selectedQuestion,
  });

  // Reputation: count upvotes across user's answers
  const { data: reputationData = [] } = useQuery({
    queryKey: ["forum-reputation"],
    queryFn: async () => {
      const { data, error } = await supabase.from("forum_answers").select("author_user_id, author_name, author_role, upvotes");
      if (error) throw error;
      const map = new Map<string, { name: string; role: string; score: number; answers: number }>();
      (data || []).forEach((a) => {
        const existing = map.get(a.author_user_id) || { name: a.author_name, role: a.author_role, score: 0, answers: 0 };
        existing.score += a.upvotes;
        existing.answers += 1;
        map.set(a.author_user_id, existing);
      });
      return Array.from(map.entries()).map(([id, v]) => ({ id, ...v })).sort((a, b) => b.score - a.score).slice(0, 10);
    },
  });

  const askMutation = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error("Not logged in");
      const { error } = await supabase.from("forum_questions").insert({
        author_user_id: user.id,
        author_name: user.name,
        author_role: role,
        title: newQ.title.trim(),
        content: newQ.content.trim(),
        category: newQ.category,
        tags: newQ.tags.split(",").map((t) => t.trim()).filter(Boolean),
      });
      if (error) throw error;
    },
    onSuccess: () => {
      toast({ title: "Question posted!" });
      setShowAsk(false);
      setNewQ({ title: "", content: "", category: "general", tags: "" });
      queryClient.invalidateQueries({ queryKey: ["forum-questions"] });
    },
    onError: () => toast({ title: "Error posting question", variant: "destructive" }),
  });

  const answerMutation = useMutation({
    mutationFn: async () => {
      if (!user || !selectedQuestion) throw new Error("Missing data");
      const { error } = await supabase.from("forum_answers").insert({
        question_id: selectedQuestion,
        author_user_id: user.id,
        author_name: user.name,
        author_role: role,
        content: newAnswer.trim(),
      });
      if (error) throw error;
    },
    onSuccess: () => {
      toast({ title: "Answer posted!" });
      setNewAnswer("");
      queryClient.invalidateQueries({ queryKey: ["forum-answers", selectedQuestion] });
      queryClient.invalidateQueries({ queryKey: ["forum-questions"] });
      queryClient.invalidateQueries({ queryKey: ["forum-reputation"] });
    },
    onError: () => toast({ title: "Error posting answer", variant: "destructive" }),
  });

  const voteMutation = useMutation({
    mutationFn: async ({ targetType, targetId, voteType }: { targetType: string; targetId: string; voteType: string }) => {
      if (!user) throw new Error("Not logged in");
      // Upsert: delete old vote if exists, then insert
      await supabase.from("forum_votes").delete().eq("user_id", user.id).eq("target_type", targetType).eq("target_id", targetId);
      const { error } = await supabase.from("forum_votes").insert({ user_id: user.id, target_type: targetType, target_id: targetId, vote_type: voteType });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["forum-questions"] });
      queryClient.invalidateQueries({ queryKey: ["forum-answers"] });
      queryClient.invalidateQueries({ queryKey: ["forum-reputation"] });
    },
  });

  const acceptMutation = useMutation({
    mutationFn: async (answerId: string) => {
      if (!selectedQuestion) return;
      // Mark answer as accepted
      await supabase.from("forum_answers").update({ is_accepted: false }).eq("question_id", selectedQuestion);
      await supabase.from("forum_answers").update({ is_accepted: true }).eq("id", answerId);
      await supabase.from("forum_questions").update({ is_resolved: true, accepted_answer_id: answerId }).eq("id", selectedQuestion);
    },
    onSuccess: () => {
      toast({ title: "Answer accepted!" });
      queryClient.invalidateQueries({ queryKey: ["forum-answers", selectedQuestion] });
      queryClient.invalidateQueries({ queryKey: ["forum-questions"] });
    },
  });

  const filtered = questions.filter((q: any) =>
    q.title.toLowerCase().includes(searchTerm.toLowerCase()) || q.content.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedQ = questions.find((q: any) => q.id === selectedQuestion);

  const stats = {
    total: questions.length,
    resolved: questions.filter((q: any) => q.is_resolved).length,
    unanswered: questions.filter((q: any) => q.answers_count === 0).length,
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Community Forum</h1>
          <p className="text-muted-foreground text-sm">Ask questions, share knowledge, grow together</p>
        </div>
        <Dialog open={showAsk} onOpenChange={setShowAsk}>
          <DialogTrigger asChild>
            <Button><Plus className="h-4 w-4 mr-2" />Ask Question</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Ask a Question</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <Input placeholder="Question title..." value={newQ.title} onChange={(e) => setNewQ({ ...newQ, title: e.target.value })} />
              <Textarea placeholder="Describe your question in detail..." rows={5} value={newQ.content} onChange={(e) => setNewQ({ ...newQ, content: e.target.value })} />
              <Select value={newQ.category} onValueChange={(v) => setNewQ({ ...newQ, category: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
              <Input placeholder="Tags (comma separated)" value={newQ.tags} onChange={(e) => setNewQ({ ...newQ, tags: e.target.value })} />
              <Button onClick={() => askMutation.mutate()} disabled={!newQ.title.trim() || !newQ.content.trim()} className="w-full">Post Question</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: "Total Questions", value: stats.total, icon: HelpCircle },
          { label: "Resolved", value: stats.resolved, icon: CheckCircle2 },
          { label: "Unanswered", value: stats.unanswered, icon: Clock },
          { label: "Top Contributors", value: reputationData.length, icon: TrendingUp },
        ].map((s) => (
          <Card key={s.label}>
            <CardContent className="p-4 flex items-center gap-3">
              <s.icon className="h-8 w-8 text-primary" />
              <div><p className="text-2xl font-bold text-foreground">{s.value}</p><p className="text-xs text-muted-foreground">{s.label}</p></div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Tabs defaultValue="questions">
        <TabsList>
          <TabsTrigger value="questions">Questions</TabsTrigger>
          <TabsTrigger value="leaderboard">Leaderboard</TabsTrigger>
        </TabsList>

        <TabsContent value="questions" className="space-y-4">
          {/* Filters */}
          <div className="flex flex-wrap gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Search questions..." className="pl-10" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
            </div>
            <div className="flex gap-1 flex-wrap">
              <Button variant={categoryFilter === "all" ? "default" : "outline"} size="sm" onClick={() => setCategoryFilter("all")}>All</Button>
              {CATEGORIES.map((c) => (
                <Button key={c.value} variant={categoryFilter === c.value ? "default" : "outline"} size="sm" onClick={() => setCategoryFilter(c.value)}>{c.label}</Button>
              ))}
            </div>
          </div>

          {/* Question detail view */}
          {selectedQ ? (
            <div className="space-y-4">
              <Button variant="ghost" size="sm" onClick={() => setSelectedQuestion(null)}>← Back to Questions</Button>
              <Card>
                <CardHeader>
                  <div className="flex items-start justify-between gap-4">
                    <div className="space-y-2 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge variant="outline">{selectedQ.category}</Badge>
                        {selectedQ.is_resolved && <Badge className="bg-emerald-500/10 text-emerald-600 border-emerald-500/30"><CheckCircle2 className="h-3 w-3 mr-1" />Resolved</Badge>}
                        {selectedQ.tags?.map((t: string) => <Badge key={t} variant="secondary" className="text-xs">{t}</Badge>)}
                      </div>
                      <CardTitle className="text-xl">{selectedQ.title}</CardTitle>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span className="font-medium text-foreground">{selectedQ.author_name}</span>
                        {ROLE_BADGES[selectedQ.author_role] && (
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${ROLE_BADGES[selectedQ.author_role].color}`}>{ROLE_BADGES[selectedQ.author_role].label}</span>
                        )}
                        <span>•</span>
                        <span>{new Date(selectedQ.created_at).toLocaleDateString()}</span>
                        <span>•</span>
                        <Eye className="h-3 w-3" />{selectedQ.views} views
                      </div>
                    </div>
                    <div className="flex flex-col items-center gap-1">
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => voteMutation.mutate({ targetType: "question", targetId: selectedQ.id, voteType: "up" })}>
                        <ThumbsUp className="h-4 w-4" />
                      </Button>
                      <span className="text-sm font-bold text-foreground">{selectedQ.upvotes - selectedQ.downvotes}</span>
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => voteMutation.mutate({ targetType: "question", targetId: selectedQ.id, voteType: "down" })}>
                        <ThumbsDown className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-foreground whitespace-pre-wrap">{selectedQ.content}</p>
                </CardContent>
              </Card>

              {/* Answers */}
              <h3 className="text-lg font-semibold text-foreground">{answers.length} Answer{answers.length !== 1 ? "s" : ""}</h3>
              {answers.map((a: any) => (
                <Card key={a.id} className={a.is_accepted ? "border-emerald-500/50 bg-emerald-500/5" : ""}>
                  <CardContent className="p-4">
                    <div className="flex gap-4">
                      <div className="flex flex-col items-center gap-1">
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => voteMutation.mutate({ targetType: "answer", targetId: a.id, voteType: "up" })}>
                          <ThumbsUp className="h-3.5 w-3.5" />
                        </Button>
                        <span className="text-sm font-bold text-foreground">{a.upvotes - a.downvotes}</span>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => voteMutation.mutate({ targetType: "answer", targetId: a.id, voteType: "down" })}>
                          <ThumbsDown className="h-3.5 w-3.5" />
                        </Button>
                        {a.is_accepted && <CheckCircle2 className="h-5 w-5 text-emerald-500 mt-1" />}
                      </div>
                      <div className="flex-1 space-y-2">
                        <p className="text-sm text-foreground whitespace-pre-wrap">{a.content}</p>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <span className="font-medium text-foreground">{a.author_name}</span>
                          {ROLE_BADGES[a.author_role] && (
                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${ROLE_BADGES[a.author_role].color}`}>{ROLE_BADGES[a.author_role].label}</span>
                          )}
                          <span>•</span>
                          <span>{new Date(a.created_at).toLocaleDateString()}</span>
                          {user?.id === selectedQ.author_user_id && !a.is_accepted && (
                            <Button variant="outline" size="sm" className="ml-auto text-xs h-6" onClick={() => acceptMutation.mutate(a.id)}>
                              <CheckCircle2 className="h-3 w-3 mr-1" />Accept
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}

              {/* Post answer */}
              <Card>
                <CardContent className="p-4 space-y-3">
                  <h4 className="font-medium text-foreground">Your Answer</h4>
                  <Textarea placeholder="Write your answer..." rows={4} value={newAnswer} onChange={(e) => setNewAnswer(e.target.value)} />
                  <Button onClick={() => answerMutation.mutate()} disabled={!newAnswer.trim()}>Post Answer</Button>
                </CardContent>
              </Card>
            </div>
          ) : (
            /* Question list */
            <div className="space-y-3">
              {filtered.length === 0 && <p className="text-center text-muted-foreground py-12">No questions found. Be the first to ask!</p>}
              {filtered.map((q: any) => (
                <Card key={q.id} className="cursor-pointer hover:bg-muted/30 transition-colors" onClick={() => setSelectedQuestion(q.id)}>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-4">
                      <div className="flex flex-col items-center min-w-[60px] text-center">
                        <span className="text-lg font-bold text-foreground">{q.upvotes - q.downvotes}</span>
                        <span className="text-[10px] text-muted-foreground">votes</span>
                      </div>
                      <div className={`flex flex-col items-center min-w-[60px] text-center rounded-md p-1.5 ${q.answers_count > 0 ? (q.is_resolved ? "bg-emerald-500/10 text-emerald-600" : "bg-primary/10 text-primary") : "text-muted-foreground"}`}>
                        <span className="text-lg font-bold">{q.answers_count}</span>
                        <span className="text-[10px]">answers</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <h3 className="font-semibold text-foreground text-sm truncate">{q.title}</h3>
                          {q.is_resolved && <CheckCircle2 className="h-4 w-4 text-emerald-500 flex-shrink-0" />}
                        </div>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground flex-wrap">
                          <Badge variant="outline" className="text-[10px] h-5">{q.category}</Badge>
                          <span>{q.author_name}</span>
                          {ROLE_BADGES[q.author_role] && (
                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${ROLE_BADGES[q.author_role].color}`}>{ROLE_BADGES[q.author_role].label}</span>
                          )}
                          <span>{new Date(q.created_at).toLocaleDateString()}</span>
                          {q.tags?.slice(0, 3).map((t: string) => <Badge key={t} variant="secondary" className="text-[10px] h-5">{t}</Badge>)}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="leaderboard">
          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2"><TrendingUp className="h-5 w-5 text-primary" />Top Contributors</CardTitle></CardHeader>
            <CardContent>
              {reputationData.length === 0 && <p className="text-muted-foreground text-sm text-center py-8">No contributors yet. Answer questions to appear here!</p>}
              <div className="space-y-3">
                {reputationData.map((u, i) => (
                  <div key={u.id} className="flex items-center gap-4 p-3 rounded-lg bg-muted/30">
                    <span className="text-lg font-bold text-muted-foreground w-8 text-center">#{i + 1}</span>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-foreground">{u.name}</span>
                        {ROLE_BADGES[u.role] && (
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${ROLE_BADGES[u.role].color}`}>{ROLE_BADGES[u.role].label}</span>
                        )}
                        <ReputationBadge score={u.score} />
                      </div>
                      <p className="text-xs text-muted-foreground">{u.answers} answers • {u.score} reputation points</p>
                    </div>
                    <div className="text-right">
                      <span className="text-xl font-bold text-primary">{u.score}</span>
                      <p className="text-[10px] text-muted-foreground">points</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
